import { IconButton, Tooltip } from '@material-ui/core';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import {
  Block as BlockIcon,
  Loop as LoopIcon,
  MoreHoriz as MoreHorizIcon,
  Search as PageviewIcon,
  Receipt as ReceiptIcon,
  GridOn as TableChartIcon,
  Timeline as TimelineIcon,
  Description as DescriptionIcon,
  Sync as SyncIcon,
  Delete as DeleteIcon,
} from '@material-ui/icons';
import { isEmpty, isFunction } from 'lodash';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import QCXMoveFocusInside from '../../../../../components/focus-lock/QCXMoveFocusInside';
import { userHasDeleteRole } from '../../../../../components/perfil/perfil-utils';
import QCXPopupState from '../../../../../components/popup-state/QCXPopupState';
import TarifarioMaritimoContext from '../../../../../contexts/pricing/tarifario/TarifarioMaritimoContext';
import { clienteActions } from '../../../../../features/cliente/clienteSlice';
import { IDLE_CONTROL } from '../../../../../features/config-control';
import { setQuery } from '../../../../../features/controller/controllerSlice';
import { setErrorFeedback } from '../../../../../features/feedback/feedbackSlice';
import QCXListItemIcon from '../../../../../shared-components/list-item-icon/QCXListItemIcon';


import {
  selectTarifario,
  selectTarifarios,
  selectTarifariosCount,
  selectHistoryModel,
  selectRefresh,
  selectStatus,
} from '../../../../../features/tarifario/tarifarioSelectors';

import {
  changeToConsultMode,
  changeToCreateMode,
  resetBackgroundMode,
  resetHistoryModel,
  resetMode,
  resetModel,
  resetRefresh,
  resetSubMode,
} from '../../../../../features/tarifario/tarifarioSlice';

import {
  inactivateByIdAsync,
  fetchAgenciamentoWithPaginationAsync,
} from '../../../../../features/tarifario/tarifarioThunks';

import { servicoActions } from '../../../../../features/servico/servicoSlice';
import { transportadoraActions } from '../../../../../features/transportadora/transportadoraSlice';
import SimpleConsultPageTemplate from '../../../../../templates/simple-consult-page/SimpleConsultPageTemplate/SimpleConsultPageTemplate';
import useConfigurePopUp from '../../../../../ts/common/hooks/popUps/useConfigurePopUp';
import { isAlternativeLoadingStatus, isRefresh } from '../../../../../utils/store/store-utils';

export default function TarifarioMaritimoConsultPage({ authInfo = {} }) {
  const { t } = useTranslation();
  const history = useHistory();

  const { status } = useContext(TarifarioMaritimoContext);

  const dispatch = useDispatch();

  const refresh = useSelector(selectRefresh);
  const tarifariosList = useSelector(selectTarifarios);
  const historyModel = useSelector(selectHistoryModel);

  useEffect(() => {
    console.log('TarifarioMaritimoConsultPage rendered');
  }, [])


  const isAlternativeLoading = useMemo(() => isAlternativeLoadingStatus(status), [status]);

  const resetModes = useCallback(() => {
    dispatch(resetMode());
    dispatch(resetBackgroundMode());
    dispatch(resetSubMode());
  }, []);

  const resetModels = useCallback(() => {
    dispatch(resetModel());
    dispatch(resetHistoryModel());
  }, []);

  useEffect(() => {
    resetModes();
    resetModels();
  }, []);

  const configureDependencies = useCallback(() => {
    dispatch(clienteActions.changeControlTo(IDLE_CONTROL));
    dispatch(servicoActions.changeControlTo(IDLE_CONTROL));
    dispatch(transportadoraActions.changeControlTo(IDLE_CONTROL));
  }, []);


  useEffect(() => {
    const checkIfExistsQuery = () => {
      if (!isEmpty(historyModel) && historyModel?.last?.numero) {
        dispatch(setQuery(historyModel?.last?.numero));
      }
    };

    checkIfExistsQuery();
  }, [historyModel]);


  const [handleActivateOrInactivateByIdClickMiddleware, operationOfActivateOrInactivateById] = useConfigurePopUp({
    primaryConfirmCallback: async (data) => {
      dispatch(inactivateByIdAsync(data));
    },
    initialOptions: {
      title: t('com.muralis.qcx.followup.desativarFollowUp'),
      message: t('com.muralis.qcx.followup.informeMotivoCancelamento'),
    },
  });

  const handleConfigureActivateOrInactivateByIdClick = useCallback(
    (row, popupState) => {
      const { id } = row;

      const handleActivateOrInactivateByIdClick = (event) => {
        if (isFunction(event?.stopPropagation)) {
          popupState.close(event);

          event.stopPropagation();

          const dataOfOperationActivateOrInactivate = {
            id,
          };

          handleActivateOrInactivateByIdClickMiddleware({ args: dataOfOperationActivateOrInactivate });
        }
      };

      return handleActivateOrInactivateByIdClick;
    },
    [handleActivateOrInactivateByIdClickMiddleware]
  );

  const handleButtonNewClick = useCallback(
    (event) => {
      event.stopPropagation();

      configureDependencies();

      dispatch(changeToCreateMode());

      history.push(t('com.muralis.qcx.url.pricingTarifarioMaritimoNovo'));
    },
    [history, configureDependencies]
  );

  const handleClickOnEditButton = useCallback(
    (_event, id) => history.push(t('com.muralis.qcx.url.moduloTarifarioMaritimoDetalhes', { id })),
    [history]
  );


  const columns = useMemo(
    () => [
      {
        field: 'origem',
        headerName: "Origem",
        headerAlign: 'center',
        align: 'center',
        flex: 80,
        valueGetter: ({ row }) => row?.origem?.description || '-',
      },
      {
        field: 'destino',
        headerName: 'Destino',
        flex: 80,
        valueGetter: ({ row }) => row?.destino?.description || '-',
      },
      {
        field: 'armador',
        headerName: 'Armador',
        flex: 80,
        valueGetter: ({ row }) => row?.vooEstimado || '-',
      },
      {
        field: 'servico',
        headerName: 'Serviço',
        flex: 60,
        valueGetter: ({ row }) => row?.tipoServico || '-',
      },
      {
        field: '20dry',
        headerName: "20' Dry",
        flex: 60,
        valueGetter: ({ row }) => row?.dry20 || '-',
      },
      {
        field: '40dry',
        headerName: "40' Dry",
        flex: 60,
        valueGetter: ({ row }) => row?.dry40 || '-',
      },
      {
        field: '40hc',
        headerName: "40' HC",
        flex: 60,
        valueGetter: ({ row }) => row?.tarifa300kg || '-',
      },
      {
        field: '20granel',
        headerName: "20' Granel",
        flex: 60,
        valueGetter: ({ row }) => row?.graneleiro20 || '-',
      },
      {
        field: '20flatRack',
        headerName: "20' Flat Rack",
        flex: 60,
        valueGetter: ({ row }) => row?.flatRack20 || '-',
      },
      {
        field: '40flatRack',
        headerName: "40' Flat Rack",
        flex: 60,
        valueGetter: ({ row }) => row?.flatRack40 || '-',
      },
      {
        field: 'tank',
        headerName: 'Tank',
        flex: 60,
        valueGetter: ({ row }) => row?.tank || '-',
      },
      {
        field: '20openTop',
        headerName: "20' Open Top",
        flex: 80,
        valueGetter: ({ row }) => row?.openTop20 || '-',
      },
      {
        field: '40openTop',
        headerName: "40' Open Top",
        flex: 80,
        valueGetter: ({ row }) => row?.openTop40 || '-',
      },
      {
        field: '20reefer',
        headerName: "20' Reefer",
        flex: 80,
        valueGetter: ({ row }) => row?.reefer20 || '-',
      },
      {
        field: '40reefer',
        headerName: "40' Reefer",
        flex: 80,
        valueGetter: ({ row }) => row?.reefer40 || '-',
      },
      {
        field: '20nor',
        headerName: "20' NOR",
        flex: 80,
        valueGetter: ({ row }) => row?.nor20 || '-',
      },
      {
        field: '40nor',
        headerName: "40' NOR",
        flex: 80,
        valueGetter: ({ row }) => row?.nor40 || '-',
      },
      {
        field: '20plataforma',
        headerName: "20' Plataforma",
        flex: 80,
        valueGetter: ({ row }) => row?.plataforma20 || '-',
      },
      {
        field: '40plataforma',
        headerName: "40' Plataforma",
        flex: 80,
        valueGetter: ({ row }) => row?.plataforma40 || '-',
      },
      {
        field: 'tt',
        headerName: "Transit Time",
        flex: 80,
        valueGetter: ({ row }) => row?.transitTime || '-',
      },
      {
        field: 'acoes',
        headerName: t('com.muralis.qcx.acoes.label'),
        width: 150,
        align: 'center',
        renderCell: ({ row: { id, active } }) => (
          <QCXPopupState popupId="popup-menu-catalogo-produtos">
            {(popupState) => (
              <>
                <IconButton key={`btn-more-options-${id}`} name={`btn-more-options-${id}`} {...bindTrigger(popupState)}>
                  <MoreHorizIcon color="secondary" size={20} />
                </IconButton>
                <Menu {...bindMenu(popupState)}>
                  <MenuItem
                    onClick={(event) => {
                      popupState.close(event);
  
                      handleClickOnEditButton(event, id);
                    }}
                  >
                    <QCXListItemIcon>
                      <PageviewIcon color="primary" />
                    </QCXListItemIcon>
                    <Typography
                      variant="inherit"
                      style={{
                        fontSize: 12,
                      }}
                    >
                      {t('com.muralis.qcx.acoes.consultar')}
                    </Typography>
                  </MenuItem>

                  {userHasDeleteRole(authInfo, 'catalogo-produtos') ? (
                    <MenuItem
                      onClick={(event) => {
                        popupState.close(event);
  
                        // handleActivateInactivate(event, id);
                      }}
                    >
                      <QCXListItemIcon>
                        <DeleteIcon color="error" />
                      </QCXListItemIcon>
                      <Typography
                        variant="inherit"
                        style={{
                          fontSize: 12,
                        }}
                      >
                        {t('com.muralis.qcx.acoes.excluir')}
                      </Typography>
                    </MenuItem>
                  ) : null}
                </Menu>
              </>
            )}
          </QCXPopupState>
        ),
      },
    ],
    [
      handleConfigureActivateOrInactivateByIdClick,
    ]
  );

  const breadcrumbs = [
    {
      link: {
        to: '/',
        name: t('com.muralis.qcx.inicio'),
      },
    },
    {
      link: {
        to: t('com.muralis.qcx.url.moduloPricing'),
        name: t('com.muralis.qcx.pricingModule.pricingLabel'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.pricingModule.tarifarioMaritimoLabel'),
      },
      default: true,
    },
  ];

  const handleConfigChange = useCallback(({ currentPage, pageSize, filterInputValue, advancedFilter }) => {
    const referencia = advancedFilter?.referencia ? advancedFilter?.referencia : undefined;

    dispatch(
      fetchAgenciamentoWithPaginationAsync([
        { name: 'page', value: currentPage },
        { name: 'size', value: pageSize },
        { name: 'processo', value: filterInputValue },
        { name: 'referencia', value: referencia },
        { name: 'via-transporte', value: "maritima" },
      ])
    );
  }, []);

  const rowsCount = useSelector(selectTarifariosCount);

  const templateProps = {
    page: {
      title: t('com.muralis.qcx.pricingModule.tarifarioMaritimoLabel'),
      icon: <ReceiptIcon />,
      breadcrumbs,
    },
    control: {
      buttonNew: {
        description: t('com.muralis.qcx.pricingModule.novoTarifario'),
        onClick: handleButtonNewClick,
      },
    },
    table: {
      columns,
      rowsCount,
      onConfigChangeCallback: handleConfigChange,
    },
  };

  return (
    <SimpleConsultPageTemplate
      pageProps={templateProps.page}
      controlProps={templateProps.control}
      tableProps={templateProps.table}
      tableData={tarifariosList}
      isLoading={isAlternativeLoading}
      authInfo={authInfo}
      requiredRoles={['importacao-follow-up']}
      advancedFilterKeyAccessors={['referencia']}
    >
      <></>
    </SimpleConsultPageTemplate>
  );
}
